import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import Button from "../../../../shared/ui/button/Button";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { CenterMember, CenterType } from "../../../center";
import MemberRolesModal from "./MemberRolesModal";

interface Props {
    /** The administrator looking at the list; they cannot remove themselves. */
    currentTherapistId?: string;
    members: CenterMember[];
    loading: boolean;
    type: CenterType;
    /** Needed to change roles; without it the list is read-only. */
    centerId?: string;
}

/** Rows per page of the team table. */
const PAGE_SIZE = 25;

const STATUS_STYLES: Record<string, string> = {
    active: "bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400",
    invited: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
    suspended: "bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400",
};

/**
 * Who is in the centre, and what they may do in it.
 *
 * Roles and status only. Nothing here reaches into anybody's caseload: an
 * administrator runs the practice, and reading its clinical record is not part
 * of running it.
 *
 * Removing somebody opens its own screen, where whatever they treat in the
 * centre is handed over before they go. Changing what they do happens in
 * place: nobody is taken out and invited back for it.
 */
export default function CenterTeamCard({
    currentTherapistId,
    members,
    loading,
    type,
    centerId,
}: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [editing, setEditing] = useState<CenterMember | null>(null);
    const [offset, setOffset] = useState(0);
    // Somebody leaving can empty the last page: show the last one left.
    const pageStart =
        offset < members.length
            ? offset
            : Math.max(0, Math.floor((members.length - 1) / PAGE_SIZE) * PAGE_SIZE);
    const pageMembers = members.slice(pageStart, pageStart + PAGE_SIZE);

    const roleLabel = (role: string) =>
        role === "admin"
            ? t("center.roles.admin")
            : type === "training"
              ? t("center.roles.trainer")
              : t("center.roles.therapist");

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <h2 className="mb-1 text-lg font-semibold text-content-primary">
                {type === "training" ? t("center.team.titleTraining") : t("center.team.title")}
            </h2>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                {t("center.team.subtitle")}
            </p>

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && members.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("center.team.empty")}
                </p>
            )}

            {!loading && members.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                <th scope="col" className="py-2 pr-4 font-medium">
                                    {t("center.team.name")}
                                </th>
                                <th scope="col" className="py-2 pr-4 font-medium">
                                    {t("center.team.email")}
                                </th>
                                <th scope="col" className="py-2 pr-4 font-medium">
                                    {t("center.team.roles")}
                                </th>
                                <th scope="col" className="py-2 pr-4 font-medium">
                                    {t("center.team.status")}
                                </th>
                                <th scope="col" className="py-2 font-medium">
                                    <span className="sr-only">{t("center.team.actions")}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageMembers.map((member) => (
                                <tr
                                    key={member.membership_id}
                                    className="border-b border-gray-100 last:border-0 dark:border-gray-800"
                                >
                                    <td className="py-3 pr-4 text-content-primary">
                                        {member.first_name} {member.last_name}
                                    </td>
                                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-400">
                                        {member.email}
                                    </td>
                                    <td className="py-3 pr-4">
                                        <span className="flex flex-wrap gap-1">
                                            {member.roles.map((role) => (
                                                <span
                                                    key={role}
                                                    className="rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
                                                >
                                                    {roleLabel(role)}
                                                </span>
                                            ))}
                                        </span>
                                    </td>
                                    <td className="py-3 pr-4">
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-xs ${
                                                STATUS_STYLES[member.status] ?? STATUS_STYLES.suspended
                                            }`}
                                        >
                                            {t(`center.status.${member.status}`)}
                                        </span>
                                    </td>
                                    <td className="py-3 text-right">
                                        <span className="inline-flex flex-wrap justify-end gap-2">
                                            {centerId && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setEditing(member)}
                                                    // Starts with the visible words, so a
                                                    // voice user saying "Change roles" hits it;
                                                    // the name tells the rows apart.
                                                    aria-label={t("center.team.changeRolesFor", {
                                                        name: `${member.first_name} ${member.last_name}`,
                                                    })}
                                                >
                                                    {t("center.team.changeRoles")}
                                                </Button>
                                            )}
                                            {member.therapist_id !== currentTherapistId && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() =>
                                                        navigate(
                                                            `/center/team/${member.membership_id}/leave`
                                                        )
                                                    }
                                                >
                                                    {t("center.team.remove")}
                                                </Button>
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination
                        offset={pageStart}
                        total={members.length}
                        pageSize={PAGE_SIZE}
                        onPrev={() => setOffset(Math.max(pageStart - PAGE_SIZE, 0))}
                        onNext={() => setOffset(pageStart + PAGE_SIZE)}
                    />
                </div>
            )}

            {centerId && editing && (
                <MemberRolesModal
                    isOpen={Boolean(editing)}
                    onClose={() => setEditing(null)}
                    centerId={centerId}
                    member={editing}
                    type={type}
                    isYourself={editing.therapist_id === currentTherapistId}
                />
            )}
        </section>
    );
}
